/**
 * Pure logic of the top menu, kept out of the component so it can be
 * unit-tested without rendering: navigation entries, role filtering, platform
 * switching, initial store selection and language pick.
 */
import {
  MTM_ROUTE_PATH,
  PLATFORM,
  ROLES,
  STORE_ALL,
  type Platform,
} from '@/shared/constants';
import type { Store, StoreId } from '@/shared/config/app-config';
import { compareCodeUnits } from '@/shared/lib/compare';
import type { UserProfile } from '@/features/auth/session';

export interface NavigationLink {
  link: string;
  label: string;
}

interface NavigationEntry {
  label: string;
  roles: 'ALL' | readonly string[];
  isConfigMenu?: boolean;
}

/** An entry of the menu: a link, or a label opening a sub-menu of links. */
export type NavigationItem =
  | (NavigationEntry & { link: string; children?: undefined })
  | (NavigationEntry & {
      link?: undefined;
      children: readonly NavigationLink[];
    });

/** The links of an entry: its own, or those of its sub-menu. */
export const navigationLinks = (item: NavigationItem): string[] =>
  item.children ? item.children.map((child) => child.link) : [item.link];

export const NAVIGATION_ITEMS: Record<Platform, readonly NavigationItem[]> = {
  [PLATFORM.ABCDIOR]: [
    { link: '/main', label: 'Customizations', roles: 'ALL' },
    { link: '/products', label: 'Products', roles: [ROLES.ADMIN] },
    { link: '/product-lines', label: 'Product Lines', roles: [ROLES.ADMIN] },
    { link: '/stocks', label: 'Stocks', roles: [ROLES.ADMIN] },
    { link: '/central-stocks', label: 'Central Stocks', roles: [ROLES.ADMIN] },
    { link: '/users', label: 'Users', roles: [ROLES.ADMIN, ROLES.DHL] },
    { link: '/stores', label: 'Stores', roles: [ROLES.ADMIN] },
    { link: '/workshops', label: 'Workshops', roles: [ROLES.ADMIN] },
    { link: '/translations', label: 'Translations', roles: [ROLES.ADMIN] },
    { link: '/interface', label: 'Interface', roles: [ROLES.ADMIN] },
    { link: '/info', label: 'ABCDior Settings', roles: [ROLES.ADMIN] },
  ],
  [PLATFORM.DMS]: [
    { link: `/${MTM_ROUTE_PATH}/main`, label: 'Customizations', roles: 'ALL' },
    { link: `/${MTM_ROUTE_PATH}/users`, label: 'Users', roles: [ROLES.ADMIN] },
    {
      link: `/${MTM_ROUTE_PATH}/stores`,
      label: 'Stores',
      roles: [ROLES.ADMIN],
      isConfigMenu: true,
    },
    {
      link: `/${MTM_ROUTE_PATH}/events`,
      label: 'Events',
      roles: [ROLES.ADMIN],
    },
    {
      link: `/${MTM_ROUTE_PATH}/info`,
      label: 'Info Message',
      roles: [ROLES.ADMIN],
      isConfigMenu: true,
    },
    {
      link: `/${MTM_ROUTE_PATH}/models`,
      label: 'Models',
      roles: [ROLES.ADMIN],
      isConfigMenu: true,
    },
    {
      link: `/${MTM_ROUTE_PATH}/fabrics`,
      label: 'Fabrics',
      roles: [ROLES.ADMIN],
      isConfigMenu: true,
    },
    {
      link: `/${MTM_ROUTE_PATH}/elements`,
      label: 'Elements',
      roles: [ROLES.ADMIN],
      isConfigMenu: true,
    },
    {
      link: `/${MTM_ROUTE_PATH}/link`,
      label: 'Link Models-Elements',
      roles: [ROLES.ADMIN],
      isConfigMenu: true,
    },
    {
      link: `/${MTM_ROUTE_PATH}/translations`,
      label: 'Translations',
      roles: [ROLES.ADMIN],
      isConfigMenu: true,
    },
    {
      link: `/${MTM_ROUTE_PATH}/interface`,
      label: 'Interface',
      roles: [ROLES.ADMIN],
      isConfigMenu: true,
    },
    {
      link: `/${MTM_ROUTE_PATH}/prices`,
      label: 'Prices',
      roles: [ROLES.ADMIN],
      isConfigMenu: true,
    },
    {
      label: 'Configurator',
      roles: [ROLES.ADMIN],
      isConfigMenu: true,
      children: [
        {
          link: `/${MTM_ROUTE_PATH}/configuration/fabric-book`,
          label: 'Fabric Book',
        },
        {
          link: `/${MTM_ROUTE_PATH}/configuration/sewing-pattern`,
          label: 'Sewing Pattern',
        },
        {
          link: `/${MTM_ROUTE_PATH}/configuration/filters`,
          label: 'Filters',
        },
      ],
    },
  ],
};

export const linkInPlatform = (platform: Platform, link: string): boolean =>
  NAVIGATION_ITEMS[platform].some((item) =>
    navigationLinks(item).includes(link),
  );

export const authorizedNavigationItems = (
  platform: Platform,
  role: string,
  canAccessConfigInterfaces: boolean,
): NavigationItem[] =>
  NAVIGATION_ITEMS[platform].filter(
    (item) =>
      (item.roles === 'ALL' || item.roles.includes(role)) &&
      (canAccessConfigInterfaces || item.isConfigMenu !== true),
  );

export const userPlatforms = (
  user: Pick<UserProfile, 'abcdiorAccess' | 'dmsAccess'>,
): Platform[] => {
  const platforms: Platform[] = [];
  if (user.abcdiorAccess) platforms.push(PLATFORM.ABCDIOR);
  if (user.dmsAccess) platforms.push(PLATFORM.DMS);
  return platforms;
};

/**
 * Where to send a user who opened a page of a platform they cannot access.
 * Returns null when the current link is allowed.
 */
export const platformRedirect = (
  user: Pick<UserProfile, 'abcdiorAccess' | 'dmsAccess'>,
  currentLink: string,
): string | null => {
  const linkPlatform = (Object.keys(NAVIGATION_ITEMS) as Platform[]).find(
    (platform) => linkInPlatform(platform, currentLink),
  );
  const allowed = userPlatforms(user);
  if (linkPlatform && allowed.includes(linkPlatform)) return null;
  if (user.abcdiorAccess) return '/main';
  if (user.dmsAccess) return `/${MTM_ROUTE_PATH}/main`;
  return null;
};

/** Target of "Switch to ABC Dior" / "Switch to MTM": same screen when it exists, else the platform home. */
export const switchPlatformTarget = (
  currentPlatform: Platform,
  currentUrl: string,
  target: Platform,
): string => {
  const parts = currentUrl.split('/');
  const currentLink =
    currentPlatform === PLATFORM.ABCDIOR ? parts[1] : parts[2];
  const targetLink =
    target === PLATFORM.ABCDIOR
      ? `/${currentLink ?? ''}`
      : `/${MTM_ROUTE_PATH}/${currentLink ?? ''}`;
  if (linkInPlatform(target, targetLink)) return targetLink;
  return target === PLATFORM.ABCDIOR ? '/main' : `/${MTM_ROUTE_PATH}/main`;
};

export const sortByName = <T extends { name: string }>(
  items: readonly T[],
): T[] => [...items].sort((a, b) => compareCodeUnits(a.name, b.name));

/**
 * Which stores to select when the menu mounts:
 * saved selection filtered to existing stores; "all" for admins when all was
 * saved or nothing was saved; the user's assigned stores; else the first store.
 */
export const initialStoreSelection = (
  stores: readonly Store[],
  saved: readonly StoreId[],
  user: Pick<UserProfile, 'role' | 'assignedStores'>,
): StoreId[] => {
  const allIds = stores.map((store) => store.id);
  const allSavedSelected = saved.includes(STORE_ALL.id);
  const existingSaved = saved.filter((id) => allIds.includes(id));

  if (existingSaved.length) {
    if (allSavedSelected && user.role === ROLES.ADMIN) return allIds;
    return existingSaved;
  }
  if (!stores.length) return [];
  if (user.role === ROLES.ADMIN) return allIds;
  if (user.assignedStores)
    return allIds.filter((id) => user.assignedStores?.includes(String(id)));
  const first = allIds[0];
  return first === undefined ? [] : [first];
};

/** Selection as persisted: adds STORE_ALL.id when every store is selected. */
export const selectionToPersist = (
  stores: readonly Store[],
  selected: readonly StoreId[],
): StoreId[] => {
  const ids = stores
    .map((store) => store.id)
    .filter((id) => selected.includes(id));
  return stores.length > 0 && ids.length === stores.length
    ? [...ids, STORE_ALL.id]
    : ids;
};

export const storeDropdownTitle = (
  stores: readonly Store[],
  selected: readonly StoreId[],
  maxLen = 21,
): string => {
  const title = stores
    .filter((store) => selected.includes(store.id))
    .map((store) => store.name)
    .join(', ');
  if (!title.length) return 'No stores selected';
  return title.length > maxLen ? `${title.slice(0, maxLen - 3)}...` : title;
};

export const pickLanguage = (
  languages: readonly string[],
  userLanguage: string | undefined,
): string | null => {
  const sorted = [...languages].sort((a, b) => a.localeCompare(b));
  const match = sorted.find((language) => language === userLanguage);
  return match ?? sorted[0] ?? null;
};
